<template>
  <div v-if="$page" class="program page">
    <header class="header -column">
      <h1 v-reveal="{ delay: 550 }" class="header__subtitle">{{ $page.header.hat }}</h1>
      <HighlightedTitle
        v-reveal="{ delay: 600 }"
        :title="$page.header.title"
        class="header__title -about"
      />
    </header>

    <section class="section -mid-top row program__challenges">
      <div v-reveal="{ delay: 700 }" class="section__content -flower -pad-right row__item -span-7">
        <h2 class="section__title">{{ $page.header.subtitle }}</h2>
        <RawHtml :html="$page.header.description" />
        <a :href="$page.header.link" target="_blank" class="section__link">
          <span>{{ $page.header.label }}</span>
          <Icon id="leaves" class="button__icon" />
        </a>
      </div>

      <figure v-reveal="{ delay: 650 }" class="program__challenges-background">
        <img src="@/assets/images/program-header.svg" alt="" />
      </figure>
    </section>

    <section id="mission" class="section -centered">
      <Blob shape="c" light />
      <div v-reveal class="program__solutions__block -wave">
        <h3 v-reveal="{ delay: 500 }" class="section__subtitle">{{ $page.mission.hat }}</h3>
        <HighlightedTitle
          v-reveal="{ delay: 600 }"
          :title="$page.mission.title"
          class="section__title"
        />
      </div>
      <Video :video="$page.mission.video" v-if="$page.mission.video" />
      <Video mobile :video="$page.mission.video" v-if="$page.mission.video" />
    </section>

    <section id="solutions" class="section program__solutions">
      <div class="row -wrap">
        <div
          v-for="(solution, solutionKey) in $page.solutions.items"
          :key="solutionKey"
          v-reveal
          class="program__solutions__block"
        >
          <h3 class="section__title">{{ solution.title }}</h3>
          <RawHtml :html="solution.description" />
        </div>
      </div>
    </section>

    <section class="section -mid-top">
      <div id="vision" v-reveal class="program__solutions__block -wave -vision">
        <h3 class="section__subtitle">{{ $page.vision.hat }}</h3>
        <HighlightedTitle
          v-reveal="{ delay: 600 }"
          :title="$page.vision.title"
          class="section__title"
        />
        <a
          :href="$page.document.file?.original"
          target="_blank"
          rel="noopener"
          class="cta-tool -program"
          v-if="$page.document.file?.original"
        >
          <span class="cta-tool__icon">
            <Icon id="tool-pdf" />
          </span>

          <span class="cta-tool__label">{{ $page.document.title }}</span>
        </a>
      </div>
    </section>

    <section id="objectifs" class="section program__services">
      <h2 v-reveal class="section__title">{{ $page.goals.title }}</h2>

      <div class="row -spread program__services__list">
        <article
          v-for="(goal, goalKey) in $page.goals.items"
          :key="`objective-${goalKey}`"
          v-reveal="{ delay: 50 * goalKey }"
          class="program__services__item"
        >
          <Image
            class="program__services__item__background"
            v-if="goal.image"
            :source="goal.image"
          />
          <p>{{ goal.name }}</p>
        </article>
      </div>
    </section>

    <section id="actions" class="section program__actions">
      <blob shape="b" light />
      <h2 v-reveal class="section__title">{{ $page.actions.title }}</h2>

      <ul v-reveal="{ delay: 100 }" class="row -mobile program__actions__list">
        <li
          v-for="(item, idx) in $page.actions.items"
          :key="`action-${idx}`"
          v-reveal="{ delay: idx * 75 }"
          class="program__actions__item"
        >
          <Image
            class="program__actions__item__background"
            v-if="item.image"
            :source="item.image"
          />
          <div class="program__actions__item__content">
            <h3 class="program__actions__item__title">{{ item.name }}</h3>
            <RawHtml :html="item.description" />
            <RouterLink :to="item.link" class="section__link">
              <span>{{ item.label }}</span>
              <Icon id="leaves" />
            </RouterLink>
          </div>
        </li>
      </ul>
    </section>

    <section id="partenaires" class="section program__partners">
      <div class="row -spread">
        <div class="row__item -span-9">
          <h2 v-reveal class="section__title">{{ $page.partners.title }}</h2>
          <p class="program__partners__text">{{ $page.partners.description }}</p>
          <RouterLink v-reveal :to="{ name: 'involve' }" class="section__link">
            {{ $page.partners.label }}
            <Icon id="leaves" />
          </RouterLink>
        </div>
        <div class="program__partners-badge__container">
          <Image
            v-if="$beet.options.general?.partner"
            :source="$beet.options.general.partner"
            class="program__partners-badge"
          />
          <Image
            v-if="$beet.options.general?.expert"
            :source="$beet.options.general.expert"
            class="program__partners-badge"
          />
        </div>
      </div>

      <div
        v-for="(partnersGroup, partnersGroupKey) in $page.partners.items"
        :key="partnersGroupKey"
        class="program__partners__section"
      >
        <h2 v-if="partnersGroup.important" v-reveal class="section__title">
          {{ partnersGroup.title }}
        </h2>
        <h3 v-if="!partnersGroup.important" v-reveal class="section__subtitle">
          {{ partnersGroup.title }}
        </h3>
        <ul
          v-if="partnersGroup.flexLayout === 'partners-group-repeatable'"
          class="program__partners__involve-list"
        >
          <li
            v-for="(item, idx) in getSortedSponsor(partnersGroup.items)"
            :key="`partner${idx}`"
            class="program__partners__involve-item"
          >
            <a
              v-reveal="{ delay: 25 * idx }"
              :href="item.url"
              :aria-label="item.name"
              target="_blank"
              rel="noopener"
            >
              <Image class="program__partners__background" v-if="item.image" :source="item.image" />
            </a>
          </li>
        </ul>
        <div v-else>
          <TransitionGroup name="fade-stagger" tag="ul" class="program__partners__member-list">
            <li
              v-for="(item, idx) in getMembers(partnersGroup.items)"
              :key="`partner${idx}`"
              :data-index="idx"
              :style="{ '--stagger-index': idx }"
              class="program__partners__member-item"
            >
              <p>
                <strong
                  >{{ item.first_name }} {{ item.last_name }}, {{ item.function }} chez
                  {{ item.organization }}
                </strong>
              </p>
              <p>Expertise{{ item.expertise.includes(',') ? 's' : '' }} : {{ item.expertise }}</p>
              <a
                v-if="item.linkedin && item.linkedin !== ''"
                :href="item.linkedin"
                target="_blank"
                rel="noopener"
                class="program__partners__member-item__link"
              >
                <Icon id="linkedin" />
              </a>
            </li>
          </TransitionGroup>
          <button
            v-if="!partnersGroup.important && partnersGroup.items.length > 6"
            class="button -corner-top-left"
            @click="ShowMoreMembers"
          >
            {{ isShowingAllMembers ? 'Voir moins' : 'Voir plus' }}
          </button>
        </div>
      </div>
    </section>

    <CtaContactSection
      v-reveal="{ delay: 100 }"
      :title="$page.contact.title"
      :subtitle="$page.contact.description"
      :link-label="$page.contact.label"
    />
  </div>
</template>

<script>
import Icon from '@/components/global/Icon.vue'
import Image from '@/components/global/Image.vue'
import RawHtml from '@/components/global/RawHtml.vue'
import Blob from '@/components/graphics/Blob.vue'
import HighlightedTitle from '@/components/partials/HighlightedTitle.vue'
import Video from '@/components/partials/Video.vue'
import CtaContactSection from '@/components/sections/CtaContactSection.vue'
import { PageMetaMixin } from '@/mixins'

export default {
  name: 'About',
  beetPage: 'about',
  mixins: [PageMetaMixin],
  components: {
    Image,
    Icon,
    Video,
    CtaContactSection,
    HighlightedTitle,
    Blob,
    RawHtml,
  },
  data() {
    return {
      isShowingAllMembers: false,
    }
  },
  methods: {
    getSortedSponsor(ids) {
      return ids
        .map((id) => (this.$beet.sponsors[id] ? this.$beet.sponsors[id] : null))
        .filter((sponsor) => sponsor !== null)
    },
    getMembers(ids) {
      const members = ids
        .map((id) => (this.$beet.members[id] ? this.$beet.members[id] : null))
        .filter((member) => member !== null)

      if (!this.isShowingAllMembers) {
        return members.filter((_, idx) => idx < 6)
      }

      return members
    },
    ShowMoreMembers() {
      this.isShowingAllMembers = !this.isShowingAllMembers
    },
  },
}
</script>
